import {Component, OnInit, ChangeDetectionStrategy} from '@angular/core';
import {DomSanitizer, SafeUrl} from '@angular/platform-browser';
import {TotpSetup} from '../../../shared/models/totp';
import {ShadeToolsService} from '../../../core/services/shade-tools.service';
import {NativeService} from '../../../core/services/native.service';
import {UsersService} from '../../../core/services/users.service';
import {SessionService} from '../../../core/services/session.service';
import {HttpErrorResponse} from '@angular/common/http';
import {TitleChangedAction} from '../../../core/actions/title.actions';
import {Store} from '@ngrx/store';
import {AppState} from '../../../app/app.state';
import {AsideService} from '../../../core/services/aside.service';
import {FormsModule} from '@angular/forms';
import {NgClass} from '@angular/common';
import {SpinnerComponent} from '../../components/spinner/spinner.component';
import {DrawerGapFromTopComponent} from '../../components/drawer-gap-from-top/drawer-gap-from-top.component';
import {UtilsService} from '../../../core/services/utils.service';
import {DialogBoxButtonColor} from '../../../shared/models/dialog-box';

@Component({
  selector: 'motn-security',
  templateUrl: './security.component.html',
  styleUrls: ['./security.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    FormsModule,
    NgClass,
    SpinnerComponent,
    DrawerGapFromTopComponent,
  ],
})
export class SecurityComponent implements OnInit {
  MOTION_ARTICLE_PASSWORD: string = 'https://kb.motion.cloud/account+security/password-management';
  MOTION_ARTICLE_2FA: string = 'https://kb.motion.cloud/account+security/two-factor-authentication-2fa';

  password1: string = '';
  password2: string = '';
  passwordChanging: boolean = false;

  twoFactorAuthEnabled: boolean = false;
  twoFactorAuthPreparing: boolean = false;
  twoFactorAuthSettingUp: boolean = false;
  twoFactorAuthValidating: boolean = false;
  twoFactorAuthDisabling: boolean = false;

  totpSetup: TotpSetup = null;
  totpImgSrc: SafeUrl = '';
  totpConfirmCode: string = '';

  codeCopied: boolean = false;


  constructor(
    private usersService: UsersService,
    private nativeService: NativeService,
    private sessionService: SessionService,
    private shadeTools: ShadeToolsService,
    private domSanitizer: DomSanitizer,
    private asideService: AsideService,
    private store: Store<AppState>,
  ) {
  }


  get twoFactorDrawerOpen(): boolean {
    return this.twoFactorAuthPreparing || this.twoFactorAuthSettingUp || this.twoFactorAuthValidating;
  }


  closeTwoFactorDrawer() {
    this.twoFactorAuthPreparing = this.twoFactorAuthSettingUp = this.twoFactorAuthValidating = false;
    this.totpSetup = null;
    this.totpConfirmCode = '';
  }


  ngOnInit(): void {
    this.twoFactorAuthEnabled = this.sessionService.user.TOTPEnabled;

    this.sessionService.refreshUserInfo$().subscribe(
      (data) => {
        if (data) {
          this.twoFactorAuthEnabled = data.TOTPEnabled;
        }
      },
    );

    this.updateTitle();
    this.updateAside();
  }


  writeKeyToClipboard() {
    this.nativeService.clipboardWrite(this.totpSetup.Secret);
    this.codeCopied = true;

    setTimeout(() => {
      this.codeCopied = false;
    }, 8000);
  }


  /**
   *
   */
  promptEnableTwoFactorAuth() {
    this.twoFactorAuthPreparing = true;

    this.sessionService.enableTotp$().subscribe(
      (data) => {
        this.twoFactorAuthSettingUp = true;
        this.totpSetup = data;
        this.totpImgSrc = this.domSanitizer.bypassSecurityTrustUrl(`data:image/png;base64, ${this.totpSetup?.QRCode}`);
        this.twoFactorAuthPreparing = false;
      },
      () => {
        this.twoFactorAuthPreparing = false;
      },
    );
  }


  validateTwoFactorAuthCode() {
    this.twoFactorAuthValidating = true;

    this.sessionService.validateTotp$(this.totpConfirmCode).subscribe(
      (data) => {
        this.twoFactorAuthSettingUp = false;
        this.twoFactorAuthEnabled = true;
        this.twoFactorAuthValidating = false;
      },
      () => {
        this.shadeTools.simpleOkDialog('Wrong Code', 'The code you entered was not correct. 2FA will not be enabled until validation succeeds. Please try again.');
        this.twoFactorAuthValidating = false;
      },
    );
  }


  /**
   *
   */
  promptDisableTwoFactorAuth() {
    this.twoFactorAuthDisabling = true;

    this.shadeTools.startDialog(
      'Confirm',
      'Are you sure you want to disable two-factor authentication (2FA)?',
      [
        {
          text: 'Yes, Disable 2FA',
          callback: (confirm: string) => {
            setTimeout(() => {
              this.sessionService.disableTotp$().subscribe(
                () => {
                  this.twoFactorAuthEnabled = false;
                },
                () => {
                  this.shadeTools.simpleOkDialog('Error', 'Something went wrong when attempting to disable 2FA. Please try again');
                },
                () => {
                  this.twoFactorAuthDisabling = false;
                },
              );
            }, 0);
          },
          color: DialogBoxButtonColor.Red,
        },
        {
          text: 'Cancel',
          callback: () => {
            this.twoFactorAuthDisabling = false;
          },
          color: DialogBoxButtonColor.Alt,
        },
      ],
    );
  }


  /**
   *
   */
  attemptChangePassword() {
    const pass1 = this.password1;
    const pass2 = this.password2;

    if (pass1 !== pass2) {
      this.shadeTools.simpleOkDialog('Oops', 'The passwords did not match. Please try again.');
    } else {
      this.passwordChanging = true;

      this.usersService.changePassword$(pass1).subscribe(
        () => {
          this.shadeTools.simpleOkDialog('Success', 'Your password was changed successfully. You will need to log in again on other devices, using your new password.');
          this.password1 = '';
          this.password2 = '';
          this.nativeService.runPushRegistration().subscribe();
          this.passwordChanging = false;
        },
        (error: HttpErrorResponse) => {
          const errorMessage = UtilsService.extractErrorMessage(error.error);
          this.shadeTools.simpleOkDialog('Error', 'The following error occurred while attempting to change your password: ' + errorMessage);
          this.passwordChanging = false;
        },
      );
    }
  }


  private updateTitle() {
    this.store.dispatch(new TitleChangedAction(
      '',
      'Security',
    ));
  }


  private updateAside() {
    this.asideService.replaceContextSections([]);
  }
}
